import { Select as MantineSelect, type SelectProps } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Variant = 'default' | 'card';

type Props = Omit<SelectProps, 'variant'> & {
  variant?: Variant;
};

const LABEL_STYLES = {
  color: 'var(--mantine-color-gray-6)',
  fontSize: 'var(--mantine-font-size-xs)',
  fontWeight: FONT_WEIGHTS.semibold,
};

const VARIANT_STYLES: Record<Variant, SelectProps['styles']> = {
  default: {
    label: { ...LABEL_STYLES, marginBottom: 6 },
    input: {
      borderRadius: 12,
      border: '1px solid var(--mantine-color-gray-3)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
      color: 'black',
      fontSize: 'var(--mantine-font-size-sm)',
      fontWeight: FONT_WEIGHTS.medium,
    },
  },
  card: {
    label: LABEL_STYLES,
    input: {
      backgroundColor: 'white',
      borderRadius: 12,
      border: '1px solid white',
      color: 'var(--mantine-color-gray-10)',
      '::placeholder': { color: 'var(--mantine-color-gray-6)' },
    },
    section: { color: 'black' },
    dropdown: {
      backgroundColor: 'white',
      borderRadius: 12,
      border: '1px solid white',
    },
    option: {
      color: 'var(--mantine-color-gray-10)',
      borderRadius: 8,
    },
  },
};

export function Select({ variant = 'default', styles: stylesProp, ...props }: Props) {
  const variantStyles = VARIANT_STYLES[variant] as Record<string, object>;

  const styles =
    stylesProp && typeof stylesProp !== 'function'
      ? Object.fromEntries(
          [...new Set([...Object.keys(variantStyles), ...Object.keys(stylesProp)])].map(k => [
            k,
            { ...variantStyles[k], ...(stylesProp as Record<string, object>)[k] },
          ]),
        )
      : variantStyles;

  return <MantineSelect {...props} styles={styles} />;
}
